import PageLoader from "@hey-lady/ui/atoms/PageLoader";
import React, { lazy, Suspense } from "react";
import { createBrowserRouter, Route, RouterProvider, Routes } from "react-router-dom";
import ReferralLayout from "../layouts/ReferralLayout.tsx";
import Error404Page from "../pages/Error404Page";
import HeavyLoadPage from "../pages/HeavyLoadPage";
import { AppErrorBoundary } from "./AppErrorBoundary";
import AuthUserTrackingWrapper from "./AuthUserTrackingWrapper";
import CrispIdentitySyncWrapper from "./CrispIdentitySyncWrapper";
import { RouterErrorFallback } from "./RouterErrorFallback";
import { useServiceWorkerNavigation } from "./useServiceWorkerNavigation";

/**
 * Lazy load routes
 */
const LogoutPage = lazy(() => import("../pages/LogoutPage"));
const SignupEmailVerificationPage = lazy(
  () => import("../pages/Signup/SignupEmailVerificationPage"),
);
const SignupPage = lazy(() => import("../pages/SignupPage"));
const ChargebeeHostedPage = lazy(() => import("../pages/Signup/ChargebeeHostedPage"));
const SignupGoogleNamePage = lazy(() => import("../pages/Signup/SignupGoogleNamePage"));
const GuestPassConfirmationPage = lazy(() => import("../pages/Signup/GuestPassConfirmationPage"));
const SignupConfirmationPage = lazy(() => import("../pages/SignupConfirmationPage"));
const AuthLayout = lazy(() => import("../layouts/AuthLayout"));
const OnboardLayout = lazy(() => import("../layouts/OnboardLayout"));
const ProtectedRoutes = lazy(() => import("./ProtectedRoutes"));
const ActivateUserPage = lazy(() => import("../pages/ActivateUserPage"));
const ReferralWelcomePage = lazy(() => import("../pages/ReferralWelcomePage"));

const Router: React.FC = () => {
  useServiceWorkerNavigation();

  return (
    <AppErrorBoundary fallback={RouterErrorFallback}>
      <AuthUserTrackingWrapper>
        <CrispIdentitySyncWrapper>
          <Routes>
            <Route path="404" element={<Error404Page />} />
            <Route path="heavy-load" element={<HeavyLoadPage />} />
            <Route
              path="logout"
              element={
                <Suspense fallback={<PageLoader />}>
                  <LogoutPage />
                </Suspense>
              }
            />
            <Route
              path="auth/*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AuthLayout />
                </Suspense>
              }
            />
            <Route
              path="welcome"
              element={
                <ReferralLayout>
                  <Suspense fallback={<PageLoader />}>
                    <ReferralWelcomePage />
                  </Suspense>
                </ReferralLayout>
              }
            />

            <Route
              path="signup/:step?/*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <SignupPage />
                </Suspense>
              }
            />
            <Route
              path="signup/9"
              element={
                <Suspense fallback={<PageLoader />}>
                  <SignupEmailVerificationPage />
                </Suspense>
              }
            />
            <Route
              path="signup/guest-pass"
              element={
                <Suspense fallback={<PageLoader />}>
                  <GuestPassConfirmationPage />
                </Suspense>
              }
            />
            <Route
              path="signup/confirmation"
              element={
                <Suspense fallback={<PageLoader />}>
                  <SignupConfirmationPage />
                </Suspense>
              }
            />
            <Route
              path="signup/name"
              element={
                <Suspense fallback={<PageLoader />}>
                  <SignupGoogleNamePage />
                </Suspense>
              }
            />
            <Route
              path="signup/checkout"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ChargebeeHostedPage />
                </Suspense>
              }
            />
            <Route
              path="onboard/*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <OnboardLayout />
                </Suspense>
              }
            />
            <Route
              path="activate/:activationKey"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ActivateUserPage />
                </Suspense>
              }
            />
            <Route
              path="*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoutes />
                </Suspense>
              }
            />
          </Routes>
        </CrispIdentitySyncWrapper>
      </AuthUserTrackingWrapper>
    </AppErrorBoundary>
  );
};

const router = createBrowserRouter([{ path: "*", element: <Router /> }]);

export default () => {
  return <RouterProvider router={router} />;
};
